import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../redux/store';
import type { PlannedMeal } from '../../../redux/mealPlanSlice';
import { isEntryMeal, isRecipeMeal } from '../../../redux/mealEntry';
import { todayIso } from '../../../utils/localDate';
import {
  buildFridgeCandidates,
  buildFreezerCandidates,
  leftoverPoolRange,
  cookDateOf,
  shiftDate,
  FRIDGE_LEFTOVER_DAYS,
  LEFTOVER_CATEGORY,
  type LeftoverCandidate,
} from './leftovers';

const EMPTY_MEALS: PlannedMeal[] = [];

/** Today as YYYY-MM-DD. Re-exported so existing importers keep working. */
export { todayIso };

/** Every meal in the store, flattened once. Buckets are an implementation detail. */
function useAllMeals(): PlannedMeal[] {
  const weeks = useSelector((s: RootState) => s.mealPlan.weeks);
  return useMemo(() => Object.values(weeks).flat(), [weeks]);
}

/**
 * Fridge and freezer candidates for one target slot.
 *
 * Reads meals by *date range* rather than from the displayed week's slot grid.
 * That is the whole fix for #663: the grid could only ever express "an earlier
 * weekday of the week on screen", which excludes the weekend cook that feeds
 * Tuesday lunch.
 */
export function useLeftoverCandidates(targetDate: string | null) {
  const allMeals = useAllMeals();
  const items = useSelector((s: RootState) => s.inventory.items);

  const pool = useMemo(() => {
    if (!targetDate) return EMPTY_MEALS;
    const { start, end } = leftoverPoolRange(targetDate);
    return allMeals.filter((m) => m.scheduledFor >= start && m.scheduledFor <= end);
  }, [allMeals, targetDate]);

  const fridge = useMemo(
    () => (targetDate ? buildFridgeCandidates(pool, targetDate) : []),
    [pool, targetDate],
  );

  const freezer = useMemo(() => buildFreezerCandidates(items), [items]);

  return { fridge, freezer };
}

export interface PendingLeftover {
  sourceMealId: string;
  recipeId: string;
  cookedOn: string;
  remaining: number;
  /** Last day the fridge keeps it. */
  eatBefore: string;
}

/**
 * Fridge portions that have been declared but not yet placed anywhere.
 *
 * Drives the planner banner and the sage empty-slot hints — the push half of
 * the feature. Freezer portions are deliberately absent: they keep for months,
 * so nagging about them every time the planner opens would be noise.
 */
export function usePendingLeftovers(today: string): PendingLeftover[] {
  const allMeals = useAllMeals();

  return useMemo(() => {
    const earliest = shiftDate(today, -FRIDGE_LEFTOVER_DAYS);
    const out: PendingLeftover[] = [];

    for (const m of allMeals) {
      if (!isRecipeMeal(m) || m.status !== 'cooked') continue;
      if (m.leftoverFridgePortions === undefined || m.leftoverFridgePortions <= 0) continue;

      const cookedOn = cookDateOf(m);
      if (cookedOn < earliest || cookedOn > today) continue;

      const placed = allMeals.reduce((sum, x) => {
        if (!isEntryMeal(x) || x.entry?.type !== 'leftover') return sum;
        if (x.entry.sourceMealId !== m.id) return sum;
        return sum + (x.entry.portions ?? 1);
      }, 0);

      const remaining = m.leftoverFridgePortions - placed;
      if (remaining > 0) {
        out.push({
          sourceMealId: m.id,
          recipeId: m.recipeId,
          cookedOn,
          remaining,
          eatBefore: shiftDate(cookedOn, FRIDGE_LEFTOVER_DAYS),
        });
      }
    }

    return out.sort((a, b) => a.eatBefore.localeCompare(b.eatBefore));
  }, [allMeals, today]);
}

/**
 * The meal the capture prompt should ask about, or null.
 *
 * Triggered by state rather than wired into `markMealAsCooked`, which fires
 * from five places — one of them a bare `useEffect` in DoneView with no gesture
 * at all, where a modal would be impossible. Reading the store instead means
 * those call sites need no changes and none of them can forget to ask.
 */
export function usePendingCapture(today: string): PlannedMeal | null {
  const allMeals = useAllMeals();

  return useMemo(() => {
    const earliest = shiftDate(today, -1);
    const asked = allMeals
      .filter((m) => isRecipeMeal(m))
      .filter((m) => m.status === 'cooked')
      .filter((m) => m.leftoverFridgePortions === undefined)
      .filter((m) => !m.leftoverPromptDismissed)
      .filter((m) => {
        const on = cookDateOf(m);
        return on >= earliest && on <= today;
      })
      .sort((a, b) => cookDateOf(b).localeCompare(cookDateOf(a)));
    return asked[0] ?? null;
  }, [allMeals, today]);
}

/** Frozen leftover rows, for the pantry surfaces that want to count them. */
export function useFrozenLeftovers(): LeftoverCandidate[] {
  const items = useSelector((s: RootState) => s.inventory.items);
  return useMemo(() => buildFreezerCandidates(items), [items]);
}

export { LEFTOVER_CATEGORY };
